<template>
  <div class="columns is-centered">
    <div class="column is-full">
      <h1 class="title has-text-centered has-text-info">My current plan</h1>
      <div class="pricing-table">
        <div class="pricing-plan is-horizontal is-active">
          <div class="plan-header has-text-success">{{ plan.name }}</div>
          <div class="plan-price" v-if="!isEnterpriseUser">
            <span class="plan-price-amount">
              <span class="plan-price-currency">$</span>
              {{ currentPrice.price }}
              <span class="is-uppercase">{{ plan.stripe.currency }}</span>
            </span>
            <strong>{{ billingDetails.period }}</strong>
            <br />
            <strong class="has-text-info">{{ billingDetails.billed }}</strong>
            <p class="has-text-info" v-if="currentPrice.duration">{{ currentPrice.duration }}</p>
            <!-- {{ plan.stripe.interval }} -->
          </div>
          <div class="plan-items p-3">
            <div
              class="plan-item"
              v-for="(feature, index) in filterFeatures(plan.features)"
              :key="index"
            >
              <span class="icon has-text-primary">
                <i class="fas fa-check"></i>
              </span>
              &nbsp;
              <span>
                <span v-if= "typeof feature.value !== 'boolean'">
                  {{ convertValue(feature) }}
                </span>
                {{ feature.name }}
              </span>
            </div>
          </div>
          <div class="plan-footer" v-if="showChangePlanButton">
            <router-link
              to="/upgrade"
              class="button is-primary is-button-half-to-full"
              :disabled="user._id !== user.account.owner"
            >
              <strong>Change Plan</strong>
            </router-link>
          </div>
        </div>
      </div>
      <!-- END Pricing table -->
      <div v-if="isAccountOwner">
        <div
          class="table-container"
          v-if="user.account.subscription.coupons.length"
        >
          <div class="is-divider"></div>
          <h4 class="title is-4">Redeemed Promo Codes</h4>
          <table class="table is-full is-bordered">
            <tbody>
              <tr
                v-for="coupon in user.account.subscription.coupons"
                :key="coupon._id"
              >
                <td class="is-uppercase">{{ coupon.code }}</td>
                <td class="is-uppercase has-text-centered">
                  {{ $moment(coupon.redeemedAt).format('DD-MMM-yyyy @ HH:mm') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- END Redeemed codes table -->
        <div class="is-divider"></div>
        <div class="columns is-flex-wrap-wrap">
          <div
            v-if="user.account.subscription.plan.name !== 'Free'"
            class="column is-full-tablet is-half-desktop is-flex"
          >
            <div
              class="notification has-background-grey-light is-flex-desktop is-flex-direction-column"
            >
              <p class="has-text-light has-margin-bottom-1">
                You are currently subscribed to the
                <strong>{{ user.account.subscription.plan.name }}</strong> plan
                <small> (possible courtesy upgrade for a trial period). </small>
              </p>
              <p class="has-text-light has-margin-bottom-1">
                <small>
                  You may downgrade to a free plan if you prefer (no credit card
                  info needed). But remember you will lose access to some of our
                  more advanced features.
                </small>
              </p>
              <button
                @click="downgradeToFree()"
                class="button notification-button-to-bottom"
                :disabled="user._id !== user.account.owner"
              >
                <span>Downgrade to Free</span>
              </button>
            </div>
            <!-- END Downgrade notif -->
          </div>
          <div class="column is-full-tablet is-half-desktop is-flex">
            <div
              class="notification is-danger is-flex-desktop is-flex-direction-column"
            >
              <h4 class="title is-size-4">Danger Zone</h4>
              <p class="has-margin-bottom-1">
                Be extra careful! If you close your account you will lose all your
                documents, emails, visits and metrics.
              </p>
              <button
                class="button is-danger is-light notification-button-to-bottom"
                @click="cancelingAccount"
              >
                Close My Account
              </button>
            </div>
            <!-- END danger zone notif -->
          </div>
        </div>
        <!-- END columns for notifications-->
      </div>
    </div>
    <!-- END view -->
    <sdv-dialog v-model="showConfirmation">
      <template #header>
        <p class="modal-card-title">
          Close My Account
        </p>
      </template>
      <template #footer>
        <div class="buttons">
          <button
            @click="closeAccount"
            class="button is-danger"
            :disabled="disableConfirm"
          >
            Confirm
          </button>
          <button class="button is-info" @click="showConfirmation = false">
            Cancel
          </button>
        </div>
      </template>
      <template>
        <h3 class="title is-size-3 has-text-danger has-text-centered">
          Caution!
        </h3>
        <p class="has-margin-bottom-1">
          This action is final and it CANNOT be undone. You will lose all your
          documents, emails, visits and metrics related to this account.
        </p>
        <p>
          Do you wish to proceed?
        </p>
        <label for="confirmation-input">
          Type the word "CONFIRM" in the field below to proceed.
        </label>
        <input
          class="input"
          type="text"
          id="confirmation-input"
          placeholder="CONFIRM"
          v-model="removeConfirmation"
        />
      </template>
    </sdv-dialog>
  </div>
</template>
<script>
import numeral from '@/utils/numeral';
export default {
  name: 'SubscriptionForm',
  components: {
    SdvDialog: () => import('@/components/shared/SdvDialog')
  },
  props: ['userData'],
  data() {
    return {
      user: this.userData || {},
      showConfirmation: false,
      removeConfirmation: ''
    };
  },
  beforeMount() {
    this.$nextTick(() => {
      this.user = this.$store.getters['auth/getUser'];
    });
  },
  computed: {
    disableConfirm() {
      return Boolean(this.removeConfirmation !== 'CONFIRM');
    },
    price() {
      return amount => (amount / 100).toFixed(2);
    },
    plan() {
      const user = this.$store.getters['auth/getUser'];
      return user.account.subscription.plan;
    },
    isEnterpriseUser() {
      const user = this.$store.getters['auth/getUser'];
      const plan = user.account.subscription.plan;
      return plan.type === 'enterprise';
    },
    isAccountOwner() {
      const user = this.$store.getters['auth/getUser'];
      return user._id === user.account.owner;
    },
    showChangePlanButton() {
      return this.isAccountOwner;
    },
    billingDetails() {
      const interval = this.user.account.subscription.stripe.plan.interval;
      return {
        period: ` / ${interval}`,
        billed: `(Billed once per ${interval})`
      };
    },
    currentPrice() {
      const regularPrice = this.price(
        this.plan.stripe.unit_amount || this.plan.stripe.amount
      );
      const stripe = this.user.account.subscription.stripe;
      const coupon = stripe.discount?.coupon || null;
      const discount = coupon?.percent_off || null;
      const duration = coupon
        ? `For ${coupon.duration_in_months} months. Then $${regularPrice} ${stripe.plan.currency} / ${stripe.plan.interval}.`
        : null;
      const discountedPrice = discount
        ? (((100 - discount) / 100) * regularPrice).toFixed(2)
        : null;
      let price = discountedPrice || regularPrice;
      return {
        price,
        regularPrice,
        discount,
        duration
      };
    }
  },
  methods: {
    /**
     * Converts the object hash into an Array and filters the content.
     */
    filterFeatures(features) {
      const array = Object.keys(features).map(key => features[key]);
      const filtered = array.filter(feature => feature.value !== false);
      return filtered;
    },
    /**
     * Converts numeric values to Bytes format.
     */
    convertValue(feature) {
      let value = feature.value;
      if (feature.convert) {
        value = numeral.convert(value);
      }
      return value;
    },
    /**
     * Executes the functionality to close the user's account.
     */
    closeAccount() {
      console.log('Account closed');
      this.showConfirmation = false;
      this.$axios
        .delete(this.$API.users())
        .then(() => {
          this.$notify({
            group: 'alert',
            title: 'Success!',
            text: 'Successfully closed your account',
            type: 'success'
          });
          this.$store.commit('auth/logout');
          this.$axios.defaults.headers.common['auth'] = '';
          this.$router.push('/');
        })
        .catch(error => {
          console.log('Error while logging out ', error);
          this.$notify({
            group: 'alert',
            title: 'FAILED!',
            text:
              'Something went wrong while closing your account. Please try again later.',
            type: 'error'
          });
        });
    },
    cancelingAccount() {
      this.showConfirmation = true;
      // send event informing a user wanted to cancel.
      this.$customerly.event('canceling');
    },
    downgradeToFree() {
      this.$axios
        .patch(
          `${this.$API.subscriptions}/${this.user.account.subscription._id}/plan`,
          { plan: 'free' }
        )
        .then(async response => {
          if (response.data.success) {
            this.$notify({
              group: 'alert',
              title: 'Success!',
              text: 'Successfully updated your subscription.',
              type: 'success'
            });
            this.user = (await this.$axios.get(this.$API.me)).data;
            this.$store.commit('auth/setUser', this.user);
            if (this.$route.query && this.$route.query.from) {
              this.$router.push({ path: this.$route.query.from });
            } else {
              this.$router.push({ path: '/dashboard' });
            }
          }
        })
        .catch(error => {
          console.log('Error:', error);
          this.$notify({
            group: 'alert',
            title: 'FAILED!',
            text:
              'Something went wrong while processing your request. Please try again later.',
            type: 'error'
          });
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.pricing-plan {
  &.is-horizontal {
    &.is-active {
      .plan-items {
        display: none;
        display: flex;
        flex-wrap: wrap;
        .plan-item {
          flex: 1;
          text-align: left;
          border-bottom: none;
        }
      }
    }
  }
}
// media queries obtained from https://bulma.io/documentation/overview/responsiveness/
//  Queries for desktop monitor and below
@media screen and (max-width: 1215px) {
  .notification-button-to-bottom {
    width: 100%;
    margin-top: auto;
  }
}
@media screen and (min-width: 1216px) {
  .notification-button-to-bottom {
    width: 50%;
    margin-top: auto;
  }
}
// queries for desktop on tablets
@media screen and (max-width: 1023px) {
  .plan-footer {
    .is-button-half-to-full {
      width: 100%;
    }
  }
}
@media screen and (min-width: 1024px) {
  .plan-footer {
    .is-button-half-to-full {
      width: 50%;
    }
  }
}
// queries for mobile
@media screen and (max-width: 748px) {
  .plan-item {
    min-width: 100%;
  }
  // bulma doesnt allow us to change directions or unwrap on responsive so we add a custom class
  .changes-direction-in-tablet {

  }
}
@media screen and (min-width: 749px) {
  .plan-item {
    min-width: 50%;
    &:last-child:nth-child(odd) {
      // overwrite plan-item to left
      text-align: center !important;
    }
  }
  .changes-direction-in-tablet {

  }
}
</style>
